import type { ConnectProto } from "@racoon/api";
import type { GeofenceItem } from "../pickup-geofence";

/** Closest zoom at which every step keeps its own pin, fanned around its site. */
export const STEP_FAN_MIN_ZOOM = 16;

export type StepPinItem = GeofenceItem & {
  pinLabel?: string;
  jobStatus?: ConnectProto.JobStatus;
  fan?: { index: number; count: number };
  companyName?: string;
  /** Set on a collapsed pin: its steps' jobs, lowest step first, for the badge and tooltip. */
  stepJobs?: StepJob[];
  jobId?: bigint;
  /** Set on a collapsed pin whose steps serve several customers; companyName is then cleared. */
  customerCount?: number;
  onExpand?: () => void;
  /** The site a step belongs to; two sites can share coordinates. */
  siteKey?: string;
};

export type StepJob = { step: string; jobId?: bigint; jobStatus?: ConnectProto.JobStatus };

const byStep = (a: StepJob, b: StepJob) =>
  Number(a.step) - Number(b.step) || a.step.localeCompare(b.step);

const stepSiteKey = (item: StepPinItem) => item.siteKey ?? `${item.lat},${item.lon}`;

/**
 * Folds each fan of step pins into one pin per site, except the site the user
 * expanded. A folded pin shows its lowest step; how many more it holds is drawn as a
 * badge, and `stepJobs` lists them all for the tooltip.
 */
export function collapseStepPins<T extends StepPinItem>(
  items: T[],
  expandedKey: string | null,
  onExpand: (key: string) => void,
  /** Statuses with equal keys share a colour, e.g. completed and force-completed. */
  colourKey: (status?: ConnectProto.JobStatus) => unknown = (status) => status,
): T[] {
  const result: T[] = [];
  const collapsed = new Map<string, T>();
  // Named customers only: a job with no receiver is not a second customer.
  const customers = new Map<string, Set<string>>();
  for (const item of items) {
    const key = stepSiteKey(item);
    if (!item.fan || key === expandedKey) {
      result.push(item);
      continue;
    }
    if (item.fan.count < 2) {
      result.push({
        ...item,
        stepJobs: [{ step: item.pinLabel ?? "", jobId: item.jobId, jobStatus: item.jobStatus }],
      });
      continue;
    }
    const group = collapsed.get(key);
    if (group) {
      group.stepJobs!.push({
        step: item.pinLabel ?? "",
        jobId: item.jobId,
        jobStatus: item.jobStatus,
      });
      if (item.companyName) customers.get(key)!.add(item.companyName);
      // Mixed statuses fall back to the neutral site colour.
      if (colourKey(group.jobStatus) !== colourKey(item.jobStatus)) group.jobStatus = undefined;
      continue;
    }
    const pin: T = {
      ...item,
      id: `${item.id}-site`,
      fan: undefined,
      // One job's id would speak for all the steps folded in.
      jobId: undefined,
      pinLabel: "",
      stepJobs: [{ step: item.pinLabel ?? "", jobId: item.jobId, jobStatus: item.jobStatus }],
      onExpand: () => onExpand(key),
    };
    collapsed.set(key, pin);
    customers.set(key, new Set(item.companyName ? [item.companyName] : []));
    result.push(pin);
  }
  for (const [key, pin] of collapsed) {
    pin.stepJobs!.sort(byStep);
    pin.pinLabel = pin.stepJobs![0].step;
    const names = [...customers.get(key)!];
    if (names.length > 1) {
      pin.companyName = undefined;
      pin.customerCount = names.length;
    } else {
      pin.companyName = names[0];
    }
  }
  return result;
}
